import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, BadgeCheck, Building2, CheckCircle2, Clock, Copy, Loader2, MessageCircle,
  Phone, Router as RouterIcon, Search, ShieldCheck, User, XCircle,
} from "lucide-react";
import { api, errMsg, rp, waLink } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const periodLabel = (months) => (months === 1 ? "/bulan" : months === 12 ? "/tahun" : `/${months} bulan`);

const Field = ({ label, icon: Icon, hint, ...props }) => (
  <div className="space-y-2">
    <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</Label>
    <div className="relative">
      {Icon ? <Icon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /> : null}
      <Input className={`h-11 rounded-xl bg-secondary/60 ${Icon ? "pl-9" : ""}`} {...props} />
    </div>
    {hint ? <p className="text-[11px] text-muted-foreground">{hint}</p> : null}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    pending: { t: "Menunggu konfirmasi pembayaran", c: "border-amber-500/40 bg-amber-500/10 text-amber-300", I: Clock },
    approved: { t: "Disetujui & akun aktif", c: "border-emerald-500/40 bg-emerald-500/10 text-emerald-300", I: CheckCircle2 },
    rejected: { t: "Ditolak", c: "border-red-500/40 bg-red-500/10 text-red-300", I: XCircle },
  };
  const s = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[11px] ${s.c}`} data-testid="reg-status-badge">
      <s.I className="h-3.5 w-3.5" /> {s.t}
    </span>
  );
};

export default function Register() {
  const [info, setInfo] = useState(null);
  const [plan, setPlan] = useState("");
  const [f, setF] = useState({ business_name: "", name: "", phone: "", username: "", password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [code, setCode] = useState("");
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    api.get("/public/plans")
      .then(({ data }) => {
        setInfo(data);
        if (data.plans?.length) setPlan(data.plans[0].key);
      })
      .catch((e) => toast.error(errMsg(e)));
  }, []);

  const selected = (info?.plans || []).find((p) => p.key === plan);

  const submit = async (e) => {
    e.preventDefault();
    if (!plan) return toast.error("Pilih paket langganan dulu");
    if (f.password.length < 6) return toast.error("Password minimal 6 karakter");
    if (f.password !== f.confirm) return toast.error("Konfirmasi password tidak sama");
    if (!/^[a-z0-9._-]{3,20}$/.test(f.username.trim().toLowerCase())) {
      return toast.error("Username 3-20 karakter: huruf kecil, angka, titik, garis bawah, atau strip");
    }
    setBusy(true);
    try {
      const { data } = await api.post("/public/register", {
        business_name: f.business_name, name: f.name, phone: f.phone,
        username: f.username.trim().toLowerCase(), password: f.password, plan,
      });
      setResult(data);
      setCode(data.code);
      toast.success("Pendaftaran tersimpan. Lanjutkan pembayaran & konfirmasi WhatsApp.");
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };

  const checkStatus = async (c) => {
    const kode = String(c || code).trim().toUpperCase();
    if (!kode) return toast.error("Masukkan kode pendaftaran");
    setChecking(true);
    try {
      const { data } = await api.get(`/public/register/${kode}`);
      setResult(data);
      toast.success(`Status pendaftaran: ${data.status}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setChecking(false); }
  };

  const copy = (t) => { navigator.clipboard?.writeText(String(t || "")); toast.success("Disalin"); };
  const pay = info?.payment_info || {};
  const waNo = result?.wa_number || info?.wa_number || "";

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8">
        <header className="mb-8 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cyan-400 to-violet-600">
              <RouterIcon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-display text-lg font-semibold leading-none">{info?.app_name || "ALPHA BILL"}</p>
              <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Pendaftaran Admin</p>
            </div>
          </div>
          <Button asChild variant="outline" size="sm" className="rounded-full" data-testid="register-to-login">
            <Link to="/login"><ArrowLeft className="mr-1.5 h-3.5 w-3.5" /> Ke halaman masuk</Link>
          </Button>
        </header>

        {info && !info.registration_open && !result && (
          <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-6 text-sm text-amber-300" data-testid="register-closed">
            Pendaftaran sedang ditutup. Silakan hubungi admin melalui WhatsApp {waNo || "-"}.
          </div>
        )}

        {result ? (
          <div className="fade-up space-y-6" data-testid="register-result">
            <div className="space-y-4 rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Kode Pendaftaran</p>
                  <p className="font-mono text-2xl font-semibold text-cyan-400" data-testid="reg-code">{result.code}</p>
                </div>
                <StatusBadge status={result.status} />
              </div>
              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <p><span className="text-muted-foreground">Nama usaha:</span> {result.business_name || "-"}</p>
                <p><span className="text-muted-foreground">Username:</span> <b className="font-mono">{result.username}</b></p>
                <p><span className="text-muted-foreground">WhatsApp:</span> {result.phone}</p>
                <p><span className="text-muted-foreground">Paket:</span> {result.plan_label} · <b>{rp(result.price)}</b></p>
              </div>

              {result.status === "approved" && (
                <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-300" data-testid="reg-approved-note">
                  Pembayaran sudah dikonfirmasi dan akun Anda aktif{result.expires_at ? ` sampai ${new Date(result.expires_at).toLocaleDateString("id-ID")}` : ""}.
                  Silakan masuk memakai username & password yang Anda daftarkan.
                </div>
              )}
              {result.status === "rejected" && (
                <div className="rounded-xl border border-red-500/40 bg-red-500/5 p-4 text-sm text-red-300" data-testid="reg-rejected-note">
                  Pendaftaran ditolak{result.reject_reason ? `: ${result.reject_reason}` : ""}. Silakan hubungi admin via WhatsApp.
                </div>
              )}

              {result.status === "pending" && (
                <div className="space-y-4 rounded-xl border border-dashed border-border p-4">
                  <p className="text-sm font-medium">Langkah pembayaran</p>
                  <ol className="list-decimal space-y-2 pl-5 text-sm text-muted-foreground">
                    <li>
                      Transfer <b className="text-foreground">{rp(result.price)}</b> ke:
                      <div className="mt-2 space-y-1 rounded-xl bg-secondary/50 p-3 text-xs">
                        {pay.bank_name || pay.account_number ? (
                          <p className="flex items-center justify-between gap-2">
                            <span>{pay.bank_name} · <b className="font-mono text-foreground">{pay.account_number}</b> {pay.account_holder ? `(a.n. ${pay.account_holder})` : ""}</span>
                            <Button type="button" size="icon" variant="ghost" className="h-7 w-7" onClick={() => copy(pay.account_number)}><Copy className="h-3.5 w-3.5" /></Button>
                          </p>
                        ) : null}
                        {pay.ewallet ? <p>E-wallet: <b className="font-mono text-foreground">{pay.ewallet}</b></p> : null}
                        {!pay.bank_name && !pay.account_number && !pay.ewallet ? (
                          <p className="text-amber-400">Rekening pembayaran belum diisi admin. Silakan konfirmasi langsung via WhatsApp.</p>
                        ) : null}
                        {pay.note ? <p className="text-muted-foreground">{pay.note}</p> : null}
                      </div>
                    </li>
                    <li>Tekan tombol konfirmasi di bawah, lalu kirim pesan WhatsApp beserta bukti transfer Anda.</li>
                    <li>Setelah pembayaran dikonfirmasi admin, akun Anda langsung aktif dan bisa dipakai untuk masuk.</li>
                  </ol>
                  <div className="flex flex-wrap gap-2">
                    <Button asChild className="rounded-full bg-emerald-600 text-white hover:bg-emerald-700" data-testid="reg-wa-confirm">
                      <a href={waLink(waNo, result.wa_message)} target="_blank" rel="noreferrer">
                        <MessageCircle className="mr-1.5 h-4 w-4" /> Konfirmasi via WhatsApp
                      </a>
                    </Button>
                    <Button type="button" variant="outline" className="rounded-full" onClick={() => checkStatus(result.code)} disabled={checking} data-testid="reg-refresh-status">
                      {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1.5 h-4 w-4" /> Cek status</>}
                    </Button>
                  </div>
                </div>
              )}

              <div className="flex flex-wrap gap-2 pt-1">
                <Button asChild variant="outline" className="rounded-full" data-testid="reg-goto-login">
                  <Link to="/login">Masuk ke dasbor</Link>
                </Button>
                <Button type="button" variant="ghost" className="rounded-full text-muted-foreground" onClick={() => setResult(null)} data-testid="reg-back-form">
                  Daftar lagi / ubah data
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1.1fr_1fr]">
            <section className="space-y-4">
              <div>
                <h1 className="font-display text-2xl font-semibold tracking-tight">Kelola RT-RW Net Anda dengan ALPHA BILL</h1>
                <p className="mt-2 text-sm text-muted-foreground">
                  Daftar sebagai admin, pilih paket sewa dasbor, bayar, lalu konfirmasi via WhatsApp. Setelah dikonfirmasi, akun Anda langsung aktif —
                  tambahkan router Mikrotik Anda dan mulai kelola voucher hotspot, pelanggan FTTH, tagihan, serta laporan.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-2" data-testid="register-plans">
                {(info?.plans || []).map((p) => {
                  const active = plan === p.key;
                  return (
                    <button
                      type="button" key={p.key} onClick={() => setPlan(p.key)}
                      data-testid={`plan-${p.key}`}
                      className={`rounded-2xl border p-5 text-left transition ${active ? "border-violet-500 bg-violet-500/10 ring-1 ring-violet-500/40" : "border-border bg-card hover:border-violet-500/40"}`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">{p.label}</p>
                        {active ? <BadgeCheck className="h-4 w-4 text-violet-400" /> : null}
                      </div>
                      <p className="mt-2 font-display text-2xl font-semibold">
                        {rp(p.price)}
                        <span className="text-sm font-normal text-muted-foreground">{periodLabel(Number(p.months))}</span>
                      </p>
                      <p className="mt-2 text-xs text-muted-foreground">{p.desc}</p>
                    </button>
                  );
                })}
              </div>

              <div className="space-y-2 rounded-2xl border border-border bg-card p-5 text-xs text-muted-foreground">
                <p className="flex items-center gap-2 text-sm font-medium text-foreground"><ShieldCheck className="h-4 w-4 text-cyan-400" /> Yang Anda dapatkan</p>
                <ul className="list-disc space-y-1 pl-5">
                  <li>Dasbor sendiri: hotspot, voucher, PPPoE/FTTH, tagihan & kuitansi, laporan</li>
                  <li>1 router Mikrotik per akun admin, dikelola sendiri</li>
                  <li>Akun mitra, user, dan teknisi tak terbatas di bawah akun Anda</li>
                  <li>Masa aktif bisa diperpanjang kapan saja sebelum atau sesudah berakhir</li>
                </ul>
              </div>

              <div className="space-y-2 rounded-2xl border border-border bg-card p-5">
                <p className="text-sm font-medium">Sudah pernah mendaftar? Cek status</p>
                <div className="flex flex-wrap gap-2">
                  <Input
                    value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="REGXXXXXX"
                    className="h-11 max-w-[220px] rounded-xl bg-secondary/60 font-mono" data-testid="reg-check-code"
                  />
                  <Button type="button" variant="outline" className="h-11 rounded-full" onClick={() => checkStatus()} disabled={checking} data-testid="reg-check-submit">
                    {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="mr-1.5 h-4 w-4" /> Cek status</>}
                  </Button>
                </div>
              </div>
            </section>

            <form onSubmit={submit} className="space-y-4 rounded-2xl border border-border bg-card p-6" data-testid="register-form">
              <div>
                <p className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Data Pendaftaran</p>
                <h2 className="mt-1 font-display text-xl font-semibold">Buat akun admin</h2>
              </div>
              <Field label="Nama Usaha / RT-RW Net" icon={Building2} value={f.business_name} onChange={(e) => setF({ ...f, business_name: e.target.value })} placeholder="ALPHA NET RW 05" data-testid="reg-business" />
              <Field label="Nama Lengkap" icon={User} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nama pemilik" data-testid="reg-name" />
              <Field label="Nomor WhatsApp" icon={Phone} value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="0812xxxxxxx" hint="Dipakai untuk konfirmasi pembayaran & pemberitahuan masa aktif" data-testid="reg-phone" />
              <Field label="Username" value={f.username} onChange={(e) => setF({ ...f, username: e.target.value })} placeholder="alphanet05" hint="Huruf kecil, angka, titik, garis bawah, atau strip (3-20 karakter)" data-testid="reg-username" />
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Password" type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="reg-password" />
                <Field label="Ulangi Password" type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="reg-confirm" />
              </div>
              <div className="rounded-xl bg-secondary/50 p-3 text-xs text-muted-foreground">
                Paket dipilih: <b className="text-foreground">{selected?.label || "-"}</b> · {rp(selected?.price)}{selected ? periodLabel(Number(selected.months)) : ""}
              </div>
              <Button
                type="submit" disabled={busy || (info && !info.registration_open)} data-testid="reg-submit"
                className="h-11 w-full rounded-full bg-gradient-to-r from-cyan-500 to-violet-600 text-white"
              >
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Daftar & Lanjut Pembayaran"}
              </Button>
              <p className="text-center text-[11px] text-muted-foreground">
                Akun dibuat setelah pembayaran dikonfirmasi admin melalui WhatsApp.
              </p>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
